import { debounce } from 'lodash'
import {
    isMissingTranslationsReportingEnabled,
    reportMissingTranslations,
} from '@/config/sentryConfig'

const STORAGE_KEY = 'missing_translations_sent'
const STORAGE_TTL = 7 * 24 * 60 * 60 * 1000
const STORAGE_MAX_ENTRIES = 500
const REPORT_DELAY = 10000
const REPORT_MAX_DELAY = 30000
const MAX_KEYS_PER_REPORT = 200
const UNCONFIRMED_REASONS = ['pagehide']

const pending = new Map()
const pendingRoutes = new Set()
const pendingLocales = new Set()

let sentKeys = null
let listenersAttached = false

const readSentKeys = () => {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY)
        if (!stored) return {}

        const parsed = JSON.parse(stored)
        if (!parsed || typeof parsed !== 'object') return {}

        const oldestAllowed = Date.now() - STORAGE_TTL

        return Object.fromEntries(
            Object.entries(parsed).filter(([, sentAt]) => sentAt > oldestAllowed)
        )
    } catch (error) {
        return {}
    }
}

const writeSentKeys = () => {
    try {
        const newest = Object.entries(sentKeys)
            .sort(([, a], [, b]) => b - a)
            .slice(0, STORAGE_MAX_ENTRIES)

        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(newest)))
    } catch (error) {
        return
    }
}

const wasReported = (fullKey) => {
    if (sentKeys === null) sentKeys = readSentKeys()
    return sentKeys[fullKey] !== undefined
}

const canRememberBatch = (reason) =>
    !UNCONFIRMED_REASONS.includes(reason) && window.navigator.onLine !== false

const report = (reason) => {
    if (pending.size === 0) return

    const keys = Array.from(pending.keys()).sort().slice(0, MAX_KEYS_PER_REPORT)
    const groups = {}
    keys.forEach((key) => {
        const group = pending.get(key)
        groups[group] = (groups[group] || 0) + 1
    })

    const routes = Array.from(pendingRoutes).sort()
    const locales = Array.from(pendingLocales).sort()

    keys.forEach((key) => pending.delete(key))

    reportMissingTranslations({
        keys,
        groups,
        routes,
        locales,
        reason,
        remaining: pending.size,
    })

    if (canRememberBatch(reason)) {
        const sentAt = Date.now()
        keys.forEach((key) => {
            sentKeys[key] = sentAt
        })
        writeSentKeys()
    }

    if (pending.size > 0) {
        reportLater()
        return
    }

    pendingRoutes.clear()
    pendingLocales.clear()
}

const reportLater = debounce(() => report('timer'), REPORT_DELAY, {
    maxWait: REPORT_MAX_DELAY,
})

const reportNow = (reason) => {
    reportLater.cancel()
    report(reason)
}

const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') reportNow('hidden')
}

const onPageHide = () => reportNow('pagehide')

const attachListeners = () => {
    if (listenersAttached) return
    listenersAttached = true

    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('pagehide', onPageHide)
}

export const recordMissingTranslation = ({ fullKey, group, locale, route }) => {
    if (!fullKey) return
    if (!isMissingTranslationsReportingEnabled()) return
    if (pending.has(fullKey)) return
    if (wasReported(fullKey)) return

    pending.set(fullKey, group)
    if (route) pendingRoutes.add(route)
    if (locale) pendingLocales.add(locale)

    attachListeners()
    reportLater()
}

export const resetMissingTranslationReporter = () => {
    reportLater.cancel()
    document.removeEventListener('visibilitychange', onVisibilityChange)
    window.removeEventListener('pagehide', onPageHide)
    listenersAttached = false
    pending.clear()
    pendingRoutes.clear()
    pendingLocales.clear()
    sentKeys = null
}
